import { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext(null);
const AUTH_SESSION_EXPIRED_REQUEST = "mbt-session-expired-request";

export function requestSessionExpiry() {
  window.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_REQUEST));
}

function getUserId(value) {
  if (!value) return null;
  const rawId = value.id ?? value.userId ?? value.user_id ?? value._id ?? null;
  if (rawId == null) return null;
  const id = String(rawId).trim();
  if (!id || id.toLowerCase() === "null" || id.toLowerCase() === "undefined") return null;
  return id;
}

function getTokenPayload(tokenValue) {
  try {
    return JSON.parse(atob(tokenValue.split(".")[1] || ""));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);          // ← was authUser/setAuthUser, now matches usage below
  const [token, setToken] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // ── Rehydrate from localStorage on first load ──
  useEffect(() => {
    const savedToken = localStorage.getItem("mbt_token");
    const savedUser  = localStorage.getItem("mbt_user");
    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        const tokenPayload = getTokenPayload(savedToken);
        const repairedUser = !getUserId(parsedUser) && getUserId(tokenPayload)
          ? { ...parsedUser, id: getUserId(tokenPayload) }
          : parsedUser;
        localStorage.setItem("mbt_user", JSON.stringify(repairedUser));
        setToken(savedToken);
        setUser(repairedUser);
        setIsLoggedIn(true);
      } catch {
        localStorage.removeItem("mbt_token");
        localStorage.removeItem("mbt_user");
      }
    }
    setAuthLoading(false);
  }, []);

  function login(userObject, tokenValue) {
    const tokenPayload = getTokenPayload(tokenValue);
    const repairedUser = !getUserId(userObject) && getUserId(tokenPayload)
      ? { ...userObject, id: getUserId(tokenPayload) }
      : userObject;
    localStorage.setItem("mbt_token", tokenValue);
    localStorage.setItem("mbt_user", JSON.stringify(repairedUser));
    setToken(tokenValue);
    setUser(repairedUser);
    setIsLoggedIn(true);
  }

 async function fetchCurrentUser(tokenValue) {
  const API_BASE = (
    process.env.REACT_APP_API_URL || "https://makebeetrip.com"
  ).replace(/\/$/, "");

  const response = await fetch(`${API_BASE}/api/auth/me`, {
    headers: {
      Authorization: `Bearer ${tokenValue}`,
    },
  });

  if (!response.ok) {
    throw new Error("Could not load authenticated user.");
  }

  const data = await response.json();

  return data.user;
}

  function logout() {
    localStorage.removeItem("mbt_token");
    localStorage.removeItem("mbt_user");
    setToken(null);
    setUser(null);
    setIsLoggedIn(false);
  }

  function expireSession() {
  localStorage.removeItem("mbt_token");
  localStorage.removeItem("mbt_user");
  setToken(null);
  setUser(null);
  setIsLoggedIn(false);

  window.dispatchEvent(new CustomEvent("mbt-session-expired"));
}

  useEffect(() => {
    window.addEventListener(AUTH_SESSION_EXPIRED_REQUEST, expireSession);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_REQUEST, expireSession);
  }, []);

  function updateUser(partialUpdate) {
    setUser((prev) => {
      const updated = { ...prev, ...partialUpdate };
      localStorage.setItem("mbt_user", JSON.stringify(updated));
      return updated;
    });
  }

  return (
    <AuthContext.Provider value={{
      user,
      authUser: user,     // ← alias so UserProfile.jsx's `const { authUser } = useAuth()` keeps working
      token, isLoggedIn, authLoading,
      login,
fetchCurrentUser,
logout,
expireSession,
updateUser,
setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
